import { Controller } from 'stimulus';
import StickyFill from 'stickyfilljs';
import gsap from 'gsap';
import toArray from '../helpers/toArray';

class SidebarScroller extends Controller {
  connect() {
    if (!this.element.children.length) {
      return;
    }

    this.minViewportWidth = this.data.has('min-viewport-width')
      ? parseInt(this.data.get('min-viewport-width'), 10)
      : 992;

    this.onWindowResizeBound = this.onWindowResize.bind(this);

    window.addEventListener('resize', this.onWindowResizeBound);

    [this.stickyElement] = this.element.children;

    SidebarScroller.initializeSticky();

    // Show element when everything is set and done...
    this.element.style.opacity = 1;

    const sidebarCircles = this.element.querySelector('.sidebarCircles');

    if (sidebarCircles) {
      this.initializeDynamicCircleImages(sidebarCircles);
    }

    this.onWindowResize();
  }

  disconnect() {
    if (typeof this.stickyElement !== 'undefined' && this.stickyElement) {
      StickyFill.removeOne(this.stickyElement);
    }

    if (typeof this.intersectionObserver !== 'undefined' && this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    window.removeEventListener('resize', this.onWindowResizeBound);
  }

  onWindowResize() {
    if (!this.stickyElement) {
      return;
    }

    if (window.innerWidth > this.minViewportWidth) {
      StickyFill.addOne(this.stickyElement);
    } else {
      StickyFill.removeOne(this.stickyElement);
    }
  }

  static initializeSticky() {
    // Polyfill position sticky even when the browser does support sticky. We
    // do this because native sticky doesn't allow sticky when inside overflow
    // containers. And there's no easy way to remove the overflow containers.
    StickyFill.forceSticky();
  }

  initializeDynamicCircleImages(sidebarCircles) {
    this.imageWrapper = sidebarCircles.querySelector('.sidebarCirclesImage');

    if (!this.imageWrapper || !('IntersectionObserver' in window)) {
      return;
    }

    const stickyImageElements = toArray(document.querySelectorAll('[data-sticky-image]'));

    const intersectionCallback = this.onIntersectionObservation.bind(this);

    this.intersectionObserver = new IntersectionObserver(intersectionCallback, {
      rootMargin: '0px',
      threshold: 0.6,
    });

    stickyImageElements.forEach((stickyImageElement) => {
      this.intersectionObserver.observe(stickyImageElement);
    });
  }

  onIntersectionObservation(entries) {
    entries.forEach((entry) => {
      this.toggleSidebarImage(entry.target, entry.isIntersecting);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  onClickInside() {
    StickyFill.refreshAll();
  }

  toggleSidebarImage(target, imageEnabled) {
    const image = target.getAttribute('data-sticky-image');

    // Make sure the attribute contains a value...
    if (!image) {
      return;
    }

    // If the image is not enabled that means we've to fade it out and remove it...
    if (!imageEnabled) {
      const imageElement = this.imageWrapper.querySelector(`img[src*="${image}"]`);

      if (imageElement) {
        SidebarScroller.toggleSidebarImageOpacity(imageElement, 0, true);
      }

      return;
    }

    SidebarScroller.preloadImage(image)
      .then(() => {
        // Create new image element
        const imageElement = SidebarScroller.createSidebarImage(image);

        // Insert image in the DOM
        this.imageWrapper.insertAdjacentElement('beforeend', imageElement);

        // Fade the new image in
        SidebarScroller.toggleSidebarImageOpacity(imageElement, 1);
      });
  }

  static toggleSidebarImageOpacity(imageElement, toOpacity = 1, deleteOnComplete = false) {
    if (!imageElement) {
      return;
    }

    gsap.to(imageElement, {
      opacity: toOpacity,
      onComplete: () => {
        if (deleteOnComplete && typeof imageElement.remove === 'function') {
          imageElement.remove();
        }
      },
    });
  }

  static preloadImage(source) {
    return new Promise((resolve) => {
      const imageElement = new Image();
      imageElement.onload = resolve;
      imageElement.src = source;
    });
  }

  static createSidebarImage(source) {
    const imageElement = document.createElement('img');
    imageElement.src = source;
    imageElement.width = 960;
    imageElement.height = 960;
    imageElement.style.opacity = '0';

    return imageElement;
  }
}

export default SidebarScroller;
